import {
  QuoteBindResponseSchema,
  QuoteCalculateResponseSchema,
  QuoteGetResponseSchema,
  QuoteListResponseSchema,
  QuoteSaveStatusResponseSchema,
  type QuoteBindRequest,
  type QuoteBindResponse,
  type QuoteCalculateRequest,
  type QuoteCalculateResponse,
  type QuoteGetResponse,
  type QuoteListResponse,
  type QuoteSaveStatusRequest,
  type QuoteSaveStatusResponse,
} from '../schemas/quote.js';
import {
  QuoteReferResponseSchema,
  QuoteReferralActionResponseSchema,
  QuoteReferralListResponseSchema,
  type QuoteReferRequest,
  type QuoteReferResponse,
  type QuoteReferralActionResponse,
  type QuoteReferralApproveRequest,
  type QuoteReferralDeclineRequest,
  type QuoteReferralListResponse,
} from '../schemas/referral.js';
import type { RequestOptions, Transport } from './transport.js';

export interface QuotesClient {
  calculate(
    request: QuoteCalculateRequest,
    options?: RequestOptions,
  ): Promise<QuoteCalculateResponse>;
  bind(request: QuoteBindRequest, options?: RequestOptions): Promise<QuoteBindResponse>;
  refer(request: QuoteReferRequest, options?: RequestOptions): Promise<QuoteReferResponse>;
  listReferrals(options?: RequestOptions): Promise<QuoteReferralListResponse>;
  approveReferral(
    id: string,
    body: QuoteReferralApproveRequest,
    options?: RequestOptions,
  ): Promise<QuoteReferralActionResponse>;
  declineReferral(
    id: string,
    body: QuoteReferralDeclineRequest,
    options?: RequestOptions,
  ): Promise<QuoteReferralActionResponse>;
  /** List quotes for the calling user (scoped by entity/permission). */
  listQuotes(options?: RequestOptions): Promise<QuoteListResponse>;
  /** Fetch a single quote with full context by ref. */
  getQuote(ref: string, options?: RequestOptions): Promise<QuoteGetResponse>;
  /** Flip a Draft quote to Quoted (Finish Quote action). */
  saveStatus(
    body: QuoteSaveStatusRequest,
    options?: RequestOptions,
  ): Promise<QuoteSaveStatusResponse>;
}

export const createQuotesClient = (transport: Transport): QuotesClient => ({
  calculate: (request, options) =>
    transport.post('/quotes/calculate', QuoteCalculateResponseSchema, request, options),
  bind: (request, options) =>
    transport.post('/quotes/bind', QuoteBindResponseSchema, request, options),
  refer: (request, options) =>
    transport.post('/quotes/refer', QuoteReferResponseSchema, request, options),
  listReferrals: (options) =>
    transport.get('/quotes/referrals', QuoteReferralListResponseSchema, options),
  approveReferral: (id, body, options) =>
    transport.post(
      `/quotes/referrals/${id}/approve`,
      QuoteReferralActionResponseSchema,
      body,
      options,
    ),
  declineReferral: (id, body, options) =>
    transport.post(
      `/quotes/referrals/${id}/decline`,
      QuoteReferralActionResponseSchema,
      body,
      options,
    ),
  listQuotes: (options) => transport.get('/quotes', QuoteListResponseSchema, options),
  getQuote: (ref, options) =>
    transport.get(`/quotes/${encodeURIComponent(ref)}`, QuoteGetResponseSchema, options),
  saveStatus: (body, options) =>
    transport.post('/quotes/save-status', QuoteSaveStatusResponseSchema, body, options),
});

export type {
  QuoteCalculateRequest,
  QuoteCalculateResponse,
  QuoteBindRequest,
  QuoteBindResponse,
  QuoteReferRequest,
  QuoteReferResponse,
  QuoteReferralListResponse,
  QuoteReferralApproveRequest,
  QuoteReferralDeclineRequest,
  QuoteReferralActionResponse,
  QuoteListResponse,
  QuoteGetResponse,
  QuoteSaveStatusRequest,
  QuoteSaveStatusResponse,
};
